import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Intent, PaymentMethod, PaymentMethodService, PaymentMethodType, PaymentService } from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { from, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { SelectedPaymentMethod, SelectPaymentMethodDialogData } from '../payment-method';
import { MatRadioModule } from '@angular/material/radio';
import { AsyncPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  KeysPipe,
  PaymentMethodDisplayComponent,
  PaymentTypeToTitlePipe,
  ScriptLoaderService,
  ScriptModel,
} from '@vendasta/smb-invoicing';
import { MatDividerModule } from '@angular/material/divider';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { UserService } from '@vendasta/business-center';
import { CustomerRecipientInterface, Order } from '@vendasta/sales-orders';
import { OrderStoreService } from '../../../../core/orders.service';
import { IAMService, UserIdentifier } from '@vendasta/iamv2';
import {
  AssociatedUserIDType,
  AssociateUserToOrderService,
} from '../../../associate-user-to-order/associate-user-to-order.service';
import { AssociateUserToOrderComponent } from '../../../associate-user-to-order/associate-user-to-order.component';
import { ChargeFailureCodeMap } from '../../../../shared/constants';

declare let Stripe; // : stripe.StripeStatic;

interface UserDisplay {
  userId: string;
  displayName: string;
}

@Component({
  imports: [
    NgIf,
    NgForOf,
    NgClass,
    AsyncPipe,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatRadioModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    GalaxyBadgeModule,
    PaymentMethodDisplayComponent,
    TranslateModule,
    PaymentTypeToTitlePipe,
    KeysPipe,
    MatInputModule,
    MatSelectModule,
    GalaxyFormFieldModule,
    AssociateUserToOrderComponent,
  ],
  providers: [TranslateService],
  templateUrl: './orders-select-payment-method-dialog.component.html',
  styleUrls: ['orders-select-payment-method-dialog.component.scss'],
})
export class OrdersSelectPaymentMethodDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  PaymentMethodType = PaymentMethodType;

  @ViewChild('cardElement') cardElement: ElementRef;

  accountId: string;
  merchantId: string;
  customerId: string;
  userId: string;
  stripeKey: string;
  submitDialogText: string;
  defaultPaymentMethod: string;
  hideExistingMethods: boolean;
  orderCustomerRecipient: CustomerRecipientInterface;
  defaultCustomerRecipientId: string;

  order$: Observable<Order>;

  paymentMethod: UntypedFormControl;
  customerRecipientForm: UntypedFormControl;
  isAddingMethod$$ = new ReplaySubject<boolean>(1);
  scriptModel$: Observable<ScriptModel>;
  intent$: Observable<Intent>;
  paymentMethods$$ = new ReplaySubject<Map<PaymentMethodType, PaymentMethod[]>>();
  paymentMethods$ = this.paymentMethods$$.asObservable();
  selectedMethod$$ = new ReplaySubject<string>();
  defaultBillingUser$: Observable<UserDisplay>;
  stripe;
  card;
  error: string;
  cardHandler = this.onChange.bind(this);

  constructor(
    private changeDetector: ChangeDetectorRef,
    private stripeScriptLoader: ScriptLoaderService,
    private paymentService: PaymentService,
    private paymentMethodService: PaymentMethodService,
    public dialogRef: MatDialogRef<OrdersSelectPaymentMethodDialogComponent, SelectedPaymentMethod>,
    private alertService: SnackbarService,
    @Inject(MAT_DIALOG_DATA) public data: SelectPaymentMethodDialogData,
    private translateService: TranslateService,
    private readonly userService: UserService,
    private readonly orderStoreService: OrderStoreService,
    private readonly iamService: IAMService,
    private readonly associateUserToOrderService: AssociateUserToOrderService,
  ) {
    this.accountId = data?.accountId;
    this.merchantId = data?.merchantId;
    this.customerId = data?.customerId;
    this.userId = data?.userId;
    this.stripeKey = data?.stripeKey;
    this.submitDialogText = data?.submitDialogText;
    this.paymentMethods$$.next(data?.paymentMethods);
    this.isAddingMethod$$.next(false);
    this.hideExistingMethods = data?.hideExistingMethods ?? false;
    this.orderCustomerRecipient = data?.orderCustomerRecipient;
    this.defaultCustomerRecipientId = data?.defaultCustomerRecipientId;
    this.defaultPaymentMethod = data?.defaultPaymentMethod;

    this.order$ = this.orderStoreService.order$;
    if (!this.defaultPaymentMethod) {
      // assume the first entry in the map is the default payment method
      const paymentMethodsMap = data?.paymentMethods;
      if (paymentMethodsMap && paymentMethodsMap.size > 0) {
        const firstEntry = paymentMethodsMap.values().next().value;
        if (firstEntry && firstEntry.length > 0) {
          this.defaultPaymentMethod = firstEntry[0]?.details?.id || '';
        }
      }
    }
    this.customerRecipientForm = this.associateUserToOrderService.customersControl;

    this.defaultBillingUser$ = of(this.defaultCustomerRecipientId).pipe(
      switchMap((defaultCustomerRecipientId) => {
        if (!defaultCustomerRecipientId) {
          return of(null);
        }
        return this.iamService.getUser(new UserIdentifier({ userId: defaultCustomerRecipientId })).pipe(
          map((user) => {
            return {
              userId: user?.userId,
              displayName: `${user?.firstName || ''} ${user?.lastName || ''} (${user?.email || ''})`.trim(),
            } as UserDisplay;
          }),
        );
      }),
    );
  }

  ngOnInit(): void {
    this.scriptModel$ = this.stripeScriptLoader.load();
    this.selectedMethod$$.next(this.defaultPaymentMethod);
    this.paymentMethod = new UntypedFormControl(
      this.defaultPaymentMethod ? this.defaultPaymentMethod : null,
      Validators.required,
    );
    this.paymentMethod.markAsTouched();
    this.intent$ = this.paymentService.prepareRetailPayment(this.merchantId, this.customerId, [PaymentMethodType.CARD]);

    if (!this.submitDialogText) {
      this.submitDialogText = this.translateService.instant('PAYMENT_METHOD_SELECTOR.SAVE');
    }
  }

  ngOnDestroy(): void {
    this.card.removeEventListener('change', this.cardHandler);
    this.card.destroy();
  }

  ngAfterViewInit(): void {
    this.scriptModel$.pipe(take(1)).subscribe(() => {
      this.stripe = Stripe(this.stripeKey, {
        stripeAccount: this.accountId,
      });
      const elements = this.stripe.elements();
      this.card = elements.create('card');
      this.card.mount(this.cardElement.nativeElement);
      this.card.addEventListener('change', this.cardHandler);
    });
  }

  selectMethod(paymentMethodId: string): void {
    this.selectedMethod$$.next(paymentMethodId);
  }

  save(): void {
    if (!this.defaultCustomerRecipientId && this.customerRecipientForm.invalid) {
      this.customerRecipientForm.markAsTouched();
      return;
    }

    if (this.paymentMethod?.value) {
      if (!this.checkUserUpdated()) {
        this.dialogRef.close({
          paymentMethodId: this.paymentMethod.value,
          newMethod: false,
          userId: undefined,
        });
      } else {
        this.associateUserToOrderService
          .createVBCUserFromCustomer$(AssociatedUserIDType.userID)
          .pipe(take(1))
          .subscribe({
            next: (user) =>
              this.dialogRef.close({
                paymentMethodId: this.paymentMethod.value,
                newMethod: false,
                userId: user.userId,
              }),
          });
      }
    } else {
      this.addPaymentMethod();
    }
  }

  onChange({ error }: any): void {
    this.error = error?.message ? error.message : '';
    this.changeDetector.detectChanges();
  }

  addPaymentMethod(): void {
    this.isAddingMethod$$.next(true);
    this.intent$.pipe(take(1)).subscribe((intent) => this.confirmCard(intent.stripeClientSecret));
  }

  handleConfirmCardError(error): void {
    const errorMessage = ChargeFailureCodeMap[error?.code] || ChargeFailureCodeMap.failed_to_add_payment_method;
    this.alertService.openErrorSnack(errorMessage);
  }

  confirmCard(clientSecret: string): void {
    const data = {
      payment_method: {
        card: this.card,
      },
    };
    if (this.userId) {
      data.payment_method['metadata'] = {
        userID: this.userId,
      };
    }
    from(this.stripe.confirmCardSetup(clientSecret, data))
      .pipe(
        map((response: any) => {
          if (response?.error) {
            throw response?.error;
          }
          return response;
        }),
        catchError((err) => {
          this.handleConfirmCardError(err);
          throw err;
        }),
        take(1),
        finalize(() => this.isAddingMethod$$.next(false)),
      )
      .subscribe((response) =>
        this.dialogRef.close({
          paymentMethodId: response?.setupIntent?.payment_method,
          newMethod: true,
          userId: this.checkUserUpdated() ? this.customerRecipientForm.value?.userId : undefined,
        }),
      );
  }

  // Checks if the current order recipient is different from selected on modal
  checkUserUpdated(): boolean {
    return (
      !this.defaultCustomerRecipientId &&
      !!this.customerRecipientForm.value?.email &&
      this.orderCustomerRecipient?.userId !== this.customerRecipientForm.value?.userId
    );
  }
}
